import { useEffect, useState, ChangeEvent, useRef, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useMachine } from '@xstate/react'
import { searchMachine } from 'machines'
import { Box, Flex } from 'theme-ui'
import { Button, Loading } from 'components'
import { InputWithAdornment } from 'components/Form'
import { BackBtn, SearchIcon } from 'components/Icon'
import { useBoolean, useClickOutside } from 'hooks'
import { SearchResults } from './'

export type SearchProps = {
  initialQuery?: string | undefined
  onSearchChange?: (query: string) => void
}

export function Search({
  initialQuery,
  onSearchChange = () => {},
}: SearchProps) {
  const [showSearch, , closeSearch, toggleSearch] = useBoolean(false)

  const [current, send] = useMachine(searchMachine)
  const [query, setQuery] = useState<string>('')
  const history = useHistory()
  const location = useLocation()
  const [showResults, setShowResults] = useState<boolean>(false)
  const searchBoxRef = useRef<HTMLDivElement>(null)

  const closeResultsTray = () => {
    showResults && setShowResults(false)
    closeSearch()
  }
  const openResultsTray = () => (showResults ? null : setShowResults(true))

  const updateSearch = useCallback(
    (value: string) => {
      setQuery(value)
      send('SEARCH_CHANGE', { query: value })
      onSearchChange(value)
    },
    [setQuery, send, onSearchChange]
  )

  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      updateSearch(initialQuery)
    }
    // eslint-disable-next-line
  }, [initialQuery, updateSearch])

  const resetSearch = () => updateSearch('')

  const cancelBtn = (
    <Box
      onClick={resetSearch}
      sx={{
        color: 'textPrimary',
        cursor: 'pointer',
        fontWeight: 600,
      }}>
      Cancel
    </Box>
  )
  const abortButton = (
    <Box
      sx={{
        width: 20,
        height: 20,
        cursor: 'pointer',
        display: ['block', 'none'],
      }}
      onClick={closeResultsTray}>
      <BackBtn size={20} />
    </Box>
  )

  useClickOutside(searchBoxRef, closeResultsTray)

  function handleInputChange({ target }: ChangeEvent<any>) {
    const { value } = target
    updateSearch(value)
  }

  function handleSearchItemClick(id: string) {
    closeResultsTray()
    history.push({
      pathname: `/user/${id}`,
      state: { edit: location, id },
    })
  }

  return (
    <>
      <Button
        sx={{
          bg: 'white',
          width: 40,
          height: 40,
          px: 0,
          py: 0,
          mr: 20,
          borderRadius: '100%',
          display: ['inline-block', 'none'],
        }}
        onClick={toggleSearch}>
        <SearchIcon size={18} />
      </Button>
      <Flex
        sx={{
          position: ['absolute', 'relative', 'relative'],
          bg: ['white', 'unset'],
          flex: ['none', '5 1 430px'],
          minWidth: 130,
          minHeight: ['100vh', '80px'],
          flexDirection: ['column', 'row'],
          alignItems: 'center',
          right: 0,
          top: 0,
          zIndex: [100, 'unset'],
          bottom: 0,
          left: 0,
          justifyContent: ['start', 'center'],
          visibility: [
            showSearch ? 'visible' : 'collapse',
            'visible',
            'visible',
          ],
        }}
        ref={searchBoxRef}>
        <Box
          sx={{
            mt: 30,
            fontSize: 2,
            fontWeight: 600,
            display: ['block', 'none'],
            width: 'calc(94vw - 12px)',
            textAlign: 'left',
          }}>
          Search For a Student
        </Box>
        <Flex
          sx={{
            top: 0,
            mt: [12, 0],
            mr: [0, 40],
            flex: 1,
            justifyContent: 'center',
            maxWidth: ['94vw', 430],
            width: ['94vw', '100%'],
          }}>
          <Box
            sx={{
              width: 'inherit',
              position: 'relative',
            }}>
            <InputWithAdornment
              variant="forms.search"
              sx={{
                position: 'relative',
                boxShadow: '0px -6px 20px rgba(0, 0, 0, .1)',
                pl: query.length ? undefined : [7, 4],
                pr: query.length ? '80px' : undefined,
              }}
              placeholder="Enter name or email"
              aria-label="search-input"
              after={query.length ? cancelBtn : <SearchIcon />}
              before={!query.length ? abortButton : null}
              isAfterClickable={!!query.length}
              isBeforeClickable
              value={query}
              onFocus={openResultsTray}
              onChange={handleInputChange}
            />
          </Box>

          <SearchResults
            results={current.context.results}
            show={
              showResults &&
              !current.matches('idle') &&
              !current.matches('debouncing')
            }
            isLoading={current.matches('searching')}
            LoadingComponent={<Loading />}
            noResults={current.matches('noResults')}
            onSearchItemClick={handleSearchItemClick}
            limit={30}
            sx={{
              width: ['inherit', 600],
              top: [113, 65],
            }}
          />
        </Flex>
      </Flex>
    </>
  )
}
